import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { BottomNavService, DslService, MenuActionsService, MenuContentItem, NavigationService, UrlService, WINDOW } from '@frontend/vanilla/core';
import { BottomNavConfig } from '@frontend/vanilla/features/bottom-nav';
import { Observable, filter, first, map, switchMap, tap } from 'rxjs';

import { NavigationStack } from './navigation-stack';
import { NavigationStackHistoryService } from './navigation-stack-history.service';

@Injectable({
    providedIn: 'root',
})
export class BottomNavigationStacksService {
    private readonly homeName = 'sportshomestack';
    private readonly window = inject(WINDOW);
    private readonly navigationService = inject(NavigationService);
    private readonly navigationStackHistoryService = inject(NavigationStackHistoryService);

    private bottomNavConfig = inject(BottomNavConfig);
    private destroyRef = inject(DestroyRef);
    private urlService = inject(UrlService);
    private menuActionsService = inject(MenuActionsService);
    private bottomNavService = inject(BottomNavService);
    private dslService = inject(DslService);
    private productUtils = inject(SportsProductUtils);

    private _isEnabled: boolean;
    private bottomNavStacks: NavigationStack[] = [];
    private homeStack?: NavigationStack;

    public init(): void {
        this.bottomNavConfig.whenReady
            .pipe(
                first(),
                switchMap(() => this.getVisibleBottomNavLinkItems()),
                map((items) => ({
                    items,
                    isEnabled: this.hasValidHomeItem(items),
                })),
                tap((x) => (this._isEnabled = x.isEnabled)),
                filter((x) => x.isEnabled),
                map((x) =>
                    x.items.map((i) => ({
                        rootUrl: this.urlService.parse(i.url).pathname,
                        name: i.name,
                        clickAction: i.clickAction,
                        history: [],
                    })),
                ),
            )
            .subscribe((stacks) => {
                this.initVanillaHighlightToggle();
                this.initStacks(stacks);
                this.listenUrlChanges();
                const activeStack = this.getActiveByCurrentUrl();
                this.setActiveStack(activeStack);
                this.navigationStackHistoryService.init(this.urlService.current().url(), activeStack);
            });
    }

    public goBackStackScoped(): void {
        this.navigationStackHistoryService.goBackStackScoped();
    }

    public get isEnabled(): boolean {
        return this._isEnabled;
    }

    private hasValidHomeItem(items: MenuContentItem[]): boolean {
        return !!items.find((x) => x.name === this.homeName && x.url && x.clickAction);
    }

    private initVanillaHighlightToggle(): void {
        this.bottomNavService.enableHighlighting = false;
        this.productUtils
            .fromSportsEnter()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => (this.bottomNavService.enableHighlighting = false));
        this.productUtils
            .fromSportsLeave()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => (this.bottomNavService.enableHighlighting = true));
    }

    private initStacks(stacks: (NavigationStack & { clickAction: string })[]): void {
        stacks.forEach((stack) => {
            this.menuActionsService.register(stack.clickAction, () => this.onBottomNavItemClick(stack));
        });

        this.bottomNavStacks = stacks;
        this.homeStack = this.bottomNavStacks.find((x) => x.name === this.homeName);
    }

    private listenUrlChanges(): void {
        this.navigationStackHistoryService.historyChange$
            .pipe(this.productUtils.filterSports(), takeUntilDestroyed(this.destroyRef))
            .subscribe((x) => this.bottomNavService.setActiveItem(x.stack.name));
    }

    private getVisibleBottomNavLinkItems(): Observable<MenuContentItem[]> {
        const linkItems = this.bottomNavConfig.items.filter((x) => !!x.url);

        return this.dslService.evaluateContent(linkItems);
    }

    private onBottomNavItemClick(navigationStack: NavigationStack): void {
        const currentUrl = this.urlService.current().url();

        const alreadyActive = navigationStack.name === this.navigationStackHistoryService.activeStack?.name;

        if (alreadyActive && currentUrl === navigationStack.rootUrl) {
            this.window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (alreadyActive) {
            this.navigationService.goTo(navigationStack.rootUrl);
            return;
        }

        this.setActiveStack(navigationStack);
        this.navigationService.goTo(navigationStack.currentUrl ?? navigationStack.rootUrl);
    }

    private getActiveByCurrentUrl(): NavigationStack {
        return this.getActiveStackByUrl(this.urlService.current().pathname);
    }

    private setActiveStack(active: NavigationStack): void {
        this.navigationStackHistoryService.activeStack = active;
        this.bottomNavService.setActiveItem(active.name);
    }

    private getActiveStackByUrl(url: string): NavigationStack {
        const activeItem = this.bottomNavStacks.find((x) => x.name !== this.homeName && this.isStackActive(x, url));

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return activeItem ?? this.homeStack!;
    }

    private isStackActive(item: NavigationStack, currentUrl: string): boolean {
        return this.getKeyStackUrlPart(currentUrl) === this.getKeyStackUrlPart(item.rootUrl);
    }

    private getKeyStackUrlPart(url?: string): string {
        if (!url) {
            return '';
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return url.replace(this.homeStack!.rootUrl, '').replace(/^\/+/, '').split('/')[0];
    }
}
